import { createListenerMiddleware } from '@reduxjs/toolkit';
import { createListenerEffect, MEDIA_ASSET_STATUS, selectApi } from '@socialive/common';
import { toast } from 'react-toastify';
import { selectMediaUploadAssets, updateMediaUploadStatus, uploadAsset } from '~/store/mediaUpload';

const POLLING_INTERVAL = 3 * 1000;
const UNSUPPORTED_CODEC_ERROR = 4006;

export const effect = createListenerEffect(
  'mediaUpload/uploadPolling<Listener>',
  async (action, { dispatch, getState, take, trigger }) => {
    const api = selectApi(getState());

    const { sessionHash } = action.payload;
    let abort = false;

    const onPoll = async () => {
      const uploadedAsset = selectMediaUploadAssets(getState())[sessionHash];
      if (!uploadedAsset) {
        abort = true;
        return;
      }

      const transcoderJob = await api(`/transcoderJobs/search/findOneBySessionHash?sessionHash=${sessionHash}`);
      const transcoderJobFailed = transcoderJob.transcoderJobStatus === MEDIA_ASSET_STATUS.ERROR;

      if (transcoderJobFailed) {
        const isUnsupportedCodec = JSON.parse(transcoderJob.message)?.outputs?.some(
          (o) => o.errorCode === UNSUPPORTED_CODEC_ERROR
        );
        const errorMessage = isUnsupportedCodec
          ? `The codec or file format for ${uploadedAsset?.name} is not supported.
          Try converting to one of our supported video formats before uploading to Socialive.`
          : `Something went wrong while uploading ${uploadedAsset?.name}. Please try again.`;
        toast.error(errorMessage);
      }

      if (transcoderJobFailed || transcoderJob.transcoderJobStatus === MEDIA_ASSET_STATUS.COMPLETED) {
        await dispatch(
          updateMediaUploadStatus({
            trigger,
            status: transcoderJobFailed ? MEDIA_ASSET_STATUS.FAILED : MEDIA_ASSET_STATUS.COMPLETED,
            sessionHash,
          })
        );
      }
    };

    const interval = setInterval(onPoll, POLLING_INTERVAL);

    const completeStatuses = new Set([MEDIA_ASSET_STATUS.COMPLETED, MEDIA_ASSET_STATUS.FAILED]);
    const untilCompleted = ({ type, payload }, state) =>
      abort ||
      (type === updateMediaUploadStatus.type &&
        completeStatuses.has(payload?.status) &&
        payload?.sessionHash === sessionHash);

    await take(untilCompleted);

    clearInterval(interval);
  }
);

const { startListening, middleware } = createListenerMiddleware();

startListening({
  actionCreator: uploadAsset.fulfilled,
  effect,
});

export default middleware;
